/**
 * Which customer-contact keys a job save should actually carry.
 *
 * `PUT /jobs/:id` is PARTIAL: it copies a field only when the body's value is
 * `!== undefined`, so an absent key leaves the stored value alone and an
 * explicit `null` clears it. The job form used to send
 *
 *     customerContactName: form.customerContactName || null
 *
 * for all three fields unconditionally, which collapses those two very
 * different instructions into one. "I never entered a contact" and "I deleted
 * the contact that was here" both went down the wire as `null`, and the server
 * cannot tell them apart — so any moment the form held a blank it should not
 * have (see the rig-change wipe this shipped alongside) became a wipe of the
 * stored contact, on a field the user never touched.
 *
 * The distinction needs the value the form was LOADED with, which is why this
 * takes two arguments rather than one:
 *
 *   - a value present now                     → send it
 *   - blank now, blank when loaded            → OMIT the key; there is nothing
 *                                               to say, and saying `null`
 *                                               claims a deletion that did not
 *                                               happen
 *   - blank now, populated when loaded        → send `null`; the user really
 *                                               did clear it, and clearing has
 *                                               to keep working
 *
 * On a CREATE there is nothing loaded, so `loaded` is three empty strings and
 * no key is ever sent as null — correct, because a job that does not exist yet
 * has no contact to delete.
 *
 * Trimmed for the emptiness test only. Whatever the user typed is sent exactly
 * as typed, per the repo's normalize-for-matching-never-for-storage rule.
 */
export interface JobContactFields {
  name: string;
  phone: string;
  email: string;
}

export const EMPTY_JOB_CONTACT: JobContactFields = { name: '', phone: '', email: '' };

/** The wire keys, in the casing `POST /jobs` and `PUT /jobs/:id` accept. */
const WIRE_KEY = {
  name: 'customerContactName',
  phone: 'customerContactPhone',
  email: 'customerContactEmail',
} as const;

export function jobContactPayloadKeys(
  current: JobContactFields,
  loaded: JobContactFields = EMPTY_JOB_CONTACT
): Record<string, string | null> {
  const payload: Record<string, string | null> = {};

  for (const field of ['name', 'phone', 'email'] as const) {
    const value = current[field] ?? '';
    const was = loaded[field] ?? '';

    if (value.trim()) {
      payload[WIRE_KEY[field]] = value;
    } else if (was.trim()) {
      // Deliberately cleared. This is the ONLY path that emits a null, and it
      // is why clearing a contact still works.
      payload[WIRE_KEY[field]] = null;
    }
    // else: omit. An absent key is "don't touch", which is the truth.
  }

  return payload;
}

/** Read a job payload's contact in either casing. See the API's asymmetry note. */
export function jobContactFrom(src: any): JobContactFields {
  return {
    name: src?.customerContactName ?? src?.customer_contact_name ?? '',
    phone: src?.customerContactPhone ?? src?.customer_contact_phone ?? '',
    email: src?.customerContactEmail ?? src?.customer_contact_email ?? '',
  };
}
